import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { WithdrawalPolicyDTO } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

const DEFAULT_WINDOW_START_MINUTE = 10 * 60;
const DEFAULT_WINDOW_END_MINUTE = 22 * 60;
const DEFAULT_WINDOW_TIMEZONE = 'Asia/Shanghai';

function minuteToTimeInput(minute: number | undefined): string {
  const normalized = typeof minute === 'number' && Number.isFinite(minute)
    ? Math.min(Math.max(Math.trunc(minute), 0), 23 * 60 + 59)
    : DEFAULT_WINDOW_START_MINUTE;
  const hour = Math.floor(normalized / 60);
  const minutePart = normalized % 60;
  return `${String(hour).padStart(2, '0')}:${String(minutePart).padStart(2, '0')}`;
}

function timeInputToMinute(value: string, fallback: number): number {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return fallback;
  }
  const hour = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return fallback;
  }
  return hour * 60 + minute;
}

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid withdrawal policy': '提现策略参数无效',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useWithdrawalPolicy() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<WithdrawalPolicyDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minimumAmountFen, setMinimumAmountFen] = useState(0);
  const [firstMinimumAmountFen, setFirstMinimumAmountFen] = useState(1000);
  const [dailyLimit, setDailyLimit] = useState(0);
  const [bankCardEnabled, setBankCardEnabled] = useState(false);
  const [alipayEnabled, setAlipayEnabled] = useState(true);
  const [windowEnabled, setWindowEnabled] = useState(false);
  const [windowStartTime, setWindowStartTime] = useState(minuteToTimeInput(DEFAULT_WINDOW_START_MINUTE));
  const [windowEndTime, setWindowEndTime] = useState(minuteToTimeInput(DEFAULT_WINDOW_END_MINUTE));
  const [windowTimezone, setWindowTimezone] = useState(DEFAULT_WINDOW_TIMEZONE);
  const [newUserRequiredCompletedTasks, setNewUserRequiredCompletedTasks] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getWithdrawalPolicy();
      setData(result);
      setMinimumAmountFen(result.minimum_amount_fen);
      setFirstMinimumAmountFen(result.first_minimum_amount_fen ?? 1000);
      setDailyLimit(result.daily_limit);
      setBankCardEnabled(Boolean(result.bank_card_enabled));
      setAlipayEnabled(Boolean(result.alipay_enabled));
      setWindowEnabled(Boolean(result.window_enabled));
      setWindowStartTime(minuteToTimeInput(result.window_start_minute ?? DEFAULT_WINDOW_START_MINUTE));
      setWindowEndTime(minuteToTimeInput(result.window_end_minute ?? DEFAULT_WINDOW_END_MINUTE));
      setWindowTimezone(result.window_timezone || DEFAULT_WINDOW_TIMEZONE);
      setNewUserRequiredCompletedTasks(Math.max(0, Math.trunc(result.new_user_required_completed_tasks ?? 0)));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await settingsService.updateWithdrawalPolicy({
        minimumAmountFen,
        firstMinimumAmountFen,
        dailyLimit,
        bankCardEnabled,
        alipayEnabled,
        windowEnabled,
        windowStartMinute: timeInputToMinute(windowStartTime, DEFAULT_WINDOW_START_MINUTE),
        windowEndMinute: timeInputToMinute(windowEndTime, DEFAULT_WINDOW_END_MINUTE),
        windowTimezone,
        newUserRequiredCompletedTasks: Math.max(0, Math.trunc(newUserRequiredCompletedTasks) || 0),
      });
      await load();
      addToast('success', '提现策略已保存');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [
    minimumAmountFen,
    firstMinimumAmountFen,
    dailyLimit,
    bankCardEnabled,
    alipayEnabled,
    windowEnabled,
    windowStartTime,
    windowEndTime,
    windowTimezone,
    newUserRequiredCompletedTasks,
    addToast,
    load,
  ]);

  return {
    data,
    loading,
    saving,
    error,
    minimumAmountFen,
    setMinimumAmountFen,
    firstMinimumAmountFen,
    setFirstMinimumAmountFen,
    dailyLimit,
    setDailyLimit,
    bankCardEnabled,
    setBankCardEnabled,
    alipayEnabled,
    setAlipayEnabled,
    windowEnabled,
    setWindowEnabled,
    windowStartTime,
    setWindowStartTime,
    windowEndTime,
    setWindowEndTime,
    windowTimezone,
    setWindowTimezone,
    newUserRequiredCompletedTasks,
    setNewUserRequiredCompletedTasks,
    save,
    reload: load,
  };
}
